import { useMemo, useState } from "react";
import { ArrowDownNarrowWide, ArrowUpNarrowWide, Trash2 } from "lucide-react";
import moment from "moment";

import MedicalHistoryEmptyState from "assets/images/medical-history-empty-state.svg";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { useMedicalHistoryDelete } from "features/patients/hooks/useMedicalHistoryDelete";

import styles from "./Table.module.css";

const SORT_OPTIONS = [
  {
    field: "dateRecorded",
    direction: "desc",
    label: "Newest first",
    icon: ArrowDownNarrowWide,
  },
  {
    field: "dateRecorded",
    direction: "asc",
    label: "Oldest first",
    icon: ArrowUpNarrowWide,
  },
];

export const Table = ({
  items,
  isLoading,
  isError,
  onRetry,
  detailsColumnWidth = "var(--medical-details-width)",
  dateRecorderColumnWidth = "minmax(130px, 1fr)",
}) => {
  const [search, setSearch] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [itemToDelete, setItemToDelete] = useState(null);

  const { mutate: deleteMedicalHistory, isPending: isDeleting } =
    useMedicalHistoryDelete();

  const filteredAndSorted = useMemo(() => {
    return (items || [])
      .filter((item) => item.idc?.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        if (!a.dateRecorded && !b.dateRecorded) return 0;
        if (!a.dateRecorded) return sortDirection === "desc" ? 1 : -1;
        if (!b.dateRecorded) return sortDirection === "desc" ? -1 : 1;
        const aVal = moment(a.dateRecorded).valueOf();
        const bVal = moment(b.dateRecorded).valueOf();
        return sortDirection === "desc" ? bVal - aVal : aVal - bVal;
      });
  }, [items, search, sortDirection]);

  const columns = useMemo(
    () => [
      {
        id: "dateRecorded",
        header: "Date Recorded",
        width: dateRecorderColumnWidth,
        render: (item) =>
          item.dateRecorded
            ? moment(item.dateRecorded).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        id: "idc",
        header: "Medical Details",
        width: detailsColumnWidth,
        render: (item) => item.idc || "-",
        getTooltipText: (item) => item.idc || "-",
      },
    ],
    [detailsColumnWidth, dateRecorderColumnWidth]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Delete",
        icon: Trash2,
        primary: true,
        onClick: (item) => setItemToDelete(item),
      },
    ],
    []
  );

  return (
    <div className={styles.container}>
    <div className={styles.tableWrapper}>
      <DataTable
        columns={columns}
        data={filteredAndSorted}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        isError={isError}
        errorLabel="this patient's medical history"
        onRetry={onRetry}
        emptyIllustration={MedicalHistoryEmptyState}
        emptyTitle="No Medical History"
        emptyText="Once a doctor adds medical history, it will appear here."
        search={{ value: search, onChange: setSearch, placeholder: "Search" }}
        sort={{ field: "dateRecorded", direction: sortDirection }}
        sortOptions={SORT_OPTIONS}
        onSortChange={(_field, direction) => setSortDirection(direction)}
        rowActions={rowActions}
        rowsPerPage={20}
        rowsOptions={[5, 10, 20, 50, 100, "All"]}
      />
      <ConfirmDialog
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={() => {
          if (itemToDelete) {
            deleteMedicalHistory(itemToDelete.id, {
              onSuccess: () => setItemToDelete(null),
            });
          }
        }}
        isLoading={isDeleting}
        title="Delete medical history"
        message="Are you sure you want to delete this medical history entry? This action cannot be undone."
        confirmLabel="Delete"
      />
    </div>
    </div>
  );
};
